import {
  StyledDialog,
  StyledDialogTitle,
} from "../components/StyledComponents/Dialog/Dialog.js";
import { useTheme } from "../ThemeContext/ThemeContext.js";
import React, { useEffect, useState } from "react";
import {
  DialogContent,
  DialogActions,
  Autocomplete,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Portal,
} from "@mui/material";
import {
  PrimaryButton,
  SecondaryButton,
  StyledIconButton,
  StyledToggleButton,
} from "../components/StyledComponents/Buttons/AuradineButtons.js";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import {
  BodyMedium,
  H8TitleMedium,
  H6HeadlineSmall,
} from "../components/StyledComponents/Typography/Typography.tsx";
import { useForm, Controller } from "react-hook-form";
import PlaceHolderButton from "../components/PlaceHolderButton/PlaceHolderButton.js";
import { usePostOrg } from "../api/queries/usePostOrg.ts";
import { useAuth0 } from "@auth0/auth0-react";
import {
  StyledCheckbox,
  StyledFormControl,
  StyledTextField,
} from "../components/StyledComponents/Inputs/Inputs.js";

import useAppContextProvider from "../AppContext/useAppContextProvider.js";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import * as api from "../api/api.js";
import { useGetSiteLists } from "../api/queries/useGetSiteLists.ts";
import { set } from "lodash";
import { useAttachDetachPermissions } from "./queries/useAttachDetachPermissions.ts";

const ResourceSelectionFormHeader = ({
  setOpen,
  formReset,
  setActiveStep,
}) => {
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1.5rem 0"}
    >
      <H6HeadlineSmall>
        What would you like to add?
      </H6HeadlineSmall>

      <StyledIconButton 
        colors={colors}
        onClick={() => {
          setOpen(false);
          formReset();
          setActiveStep(0);
        }}
      >
        <CloseIcon  />
      </StyledIconButton>
    </Box>
  );
};

const ResourceSelectionForm = ({
  onMoveForward,
  setOpen,
  setActiveStep,
  setFormData,
  formData,
}) => {
  const { control, handleSubmit, watch, setValue, reset } =
    useForm({
      defaultValues: {
        whichForm: "",
      },
    });
  const { colors } = useTheme();
  useEffect(() => {
    setValue("whichForm", formData.whichForm || null);
    // Reset the form to its initial state
  }, [setValue]);
  const formToShow = watch("whichForm");

  const onSubmit = (data) => {
    const finalData = { ...data };
    setFormData(finalData);
    onMoveForward(finalData);
  };
  return (
    <>
      <ResourceSelectionFormHeader
        formReset={reset}
        setOpen={setOpen}
        setActiveStep={setActiveStep}
      />
      <form onSubmit={handleSubmit(onMoveForward)}>
        <Box
          display={"flex"}
          flexDirection={"column"}
          justifyContent={"flex-start"}
          flexGrow={1}
          padding={0}
          gap={6}
        >
          <Box display={"flex"} flexDirection={"row"}>
            <StyledFormControl
              colors={colors}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 4,
                margin: 0,
              }}
            >
              <Controller
                name="whichForm"
                control={control}
                defaultValue={null}
                render={({
                  field: { value, onChange },
                }) => (
                  <StyledToggleButton
                    colors={colors}
                    endIcon={
                      <span className="material-symbols-outlined">
                        visibility_lock
                      </span>
                    }
                    value={"sitePermissions"}
                    onClick={() =>
                      onChange(
                        formToShow === "sitePermissions"
                          ? null
                          : "sitePermissions"
                      )
                    }
                    selected={
                      formToShow === "sitePermissions"
                    }
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                  >
                    {formToShow === "sitePermissions" ? (
                      <CheckIcon fontSize={"small"} />
                    ) : (
                      ""
                    )}{" "}
                    Site permissions
                  </StyledToggleButton>
                )}
              />
            </StyledFormControl>
          </Box>
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            flexGrow={1}
          >
            <DialogActions>
              <PrimaryButton colors={colors}
                disabled={!formToShow}
                onClick={handleSubmit(onSubmit)}
              >
                Next
              </PrimaryButton>
            </DialogActions>
          </Box>
        </Box>
      </form>
    </>
  );
};

const SitePermissionsForm = ({
  open,
  handleClose,
  onMoveBack,
  setActiveStep,
  refetch: dataRefetch,
}) => {
  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [severityLevel, setSeverityLevel] = useState("");
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();
  const [authToken, setAuthToken] = useState("");
  const { isAuthenticated, getAccessTokenSilently } =
    useAuth0(); // Use the useAuth0 hook to get authentication status and token fetching function
  const { colors } = useTheme();
  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn(
            "User is not authenticated. Fetching token..."
          );
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000); // Refresh every 15 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [getAccessTokenSilently, isAuthenticated]);

  let membersData = [];

  const {
    data: userData,
    isLoading,
    isFetching,
    isError,
    isFetched,
    refetch: userDataRefetch,
  } = useQuery({
    queryKey: ["user", open],
    queryFn: ({ queryKey }) =>
      api.GetUserUseQuery(authToken),
    enabled: !!authToken && open,
  });
  const orgId =
    isFetched && userData?.result?.org_id
      ? userData?.result?.org_id
      : "";

  const {
    data: members,
    refetch: membersDataRefetch,
    isLoading: isMembersDataLoading,
    isFetching: isMembersDataFetching,
    isFetched: isMembersDataFetched,
  } = useQuery({
    queryKey: ["membersData", orgId], // Use only serializable values
    queryFn: () => api.GetOrgUseQuery(authToken, orgId), // Pass correct arguments
    enabled: open && !!authToken,
  });

  if (isMembersDataFetched && members) {
    membersData =
      isMembersDataFetched &&
      members?.org?.user &&
      members?.org?.user.length > 0
        ? members?.org?.user
            .filter((user) => user.role !== "superadmin")
            .map((user) => user.email)
        : [];
  }

  const {
    data: siteLists,
    isLoading: isSiteGroupDataLoading,
    isFetching: isSiteGroupDataFetching,
    isFetched: isSiteGroupDataFetched,
    refetch: siteListRefetch,
  } = useGetSiteLists({
    authToken,
    leftPanelOpen: true,
  });
  const siteList =
    siteLists &&
    siteLists?.status === "ok" &&
    siteLists?.siteList?.length > 0
      ? siteLists?.siteList.map((site) => site.siteName)
      : [];

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      selectedEmails: [],
      siteConfig: [],
    },
  });

  const selectedEmails = watch("selectedEmails") || [];
  const siteOptions = watch("siteConfig") || [];
  const { mutateAsync: postPermissions } =
    useAttachDetachPermissions();

  const onSiteSubmit = async (data) => {
    const results = await Promise.allSettled(
      siteOptions.map((site) =>
        postPermissions({
          resourceType: "sites",
          resourceName: site,
          permissionOp: "attach",
          targetUsers: [...selectedEmails],
          token: authToken, // Include the token property
        })
      )
    );
    const allFullfilled = results.every(
      (result) => result.status === "fulfilled"
    );
    console.log("Results:", allFullfilled);
    if (allFullfilled) {
      setToastMessage(
        "Site permissions updated successfully"
      );
      await dataRefetch();
      setToastOpen(true);
      handleClose();
      setActiveStep(0);
      reset();
    } else {
      const errors = results
        .filter((result) => result.status === "rejected")
        .map((result) => result.reason);
      console.error("Errors:", errors);
      setToastMessage("Failed to save changes for user");
      setToastOpen(true);
    }
  };

  const handleChipDelete = (optionToDelete) => {
    const newValue = selectedEmails.filter(
      (option) => option !== optionToDelete
    );
    setValue("selectedEmails", newValue); // Update the state in react-hook-form
  };
  const handleSiteConfigDeletion = (optionToDelete) => {
    const newValue = siteOptions.filter(
      (option) => option !== optionToDelete
    );
    setValue("siteConfig", newValue);
  };

  return (
    <>
      <StyledDialogTitle colors={colors}>
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Box
            display={"flex"}
            flexDirection={"column"}
            gap={2}
          >
            <H6HeadlineSmall style={{ textAlign: "left" }}>
              Add site permissions to users.
            </H6HeadlineSmall>
            <BodyMedium style={{ textAlign: "left" }}>
              {" "}
              Select the users and what sites you want them
              to be assigned for.{" "}
            </BodyMedium>
          </Box>
          <StyledIconButton
            colors={colors}
            onClick={() => {
              handleClose();
              setActiveStep(0);
              reset();
            }}
          >
            <CloseIcon />
          </StyledIconButton>
        </Box>
      </StyledDialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        {showAlert && (
          <Portal>
            <Box
              padding={"0 0.5rem 0.5rem 0.5rem"}
              display={"flex"}
              flexGrow={0}
              sx={{
                position: "fixed",
                top: "30%",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 2000, // Ensure it appears above the dialog
                width: "auto",
              }}
            >
              <Alert
                severity={"error"}
                onClose={() => setShowAlert(false)}
                sx={{ padding: "0.5rem 0.75rem", color: colors?.primary?.main }}
              >
                {alertText}
              </Alert>
            </Box>
          </Portal>
        )}
        <form onSubmit={handleSubmit(onSiteSubmit)}>
          <Box
            display="flex"
            flexDirection="column"
            gap={6}
            flexGrow={1}
          >
            <Box
              display="flex"
              flexDirection="column"
              justifyContent={"flex-start"}
              gap={2}
              flexGrow={1}
            >
              <H8TitleMedium>
                Select whom to notify{" "}
              </H8TitleMedium>
              <Controller
                name="selectedEmails"
                control={control}
                defaultValue={null}
                rules={{
                  required: "Email selection is required",
                  validate: (value) =>
                    Array.isArray(value) && value.length > 0
                      ? true
                      : "Please select at least one email",
                }}
                render={({
                  field: { onChange, value, ref },
                }) => (
                  <Autocomplete
                    fullWidth
                    multiple
                    disableCloseOnSelect
                    onChange={(_, data) => onChange(data)}
                    value={value || []}
                    clearIcon={null}
                    ListboxProps={{
                      sx: {
                        maxHeight: "250px",
                        overflowY: "auto",
                        backgroundColor: colors.background.paper,
                                "&.selected": {
                                  backgroundColor:
                                    colors.primary.light,
                                },
                                color: colors.text.primary,
                      },
                    }}
                    slotProps={{
                      popupIndicator: {
                        sx: { color: colors.text.muted , 
                        '&:hover': { color: colors.text.primary },
                      }
                      },
                      clearIndicator: {
                        sx: {
                          color: colors.text.muted,
                          '&:hover': { color: colors.text.primary },
                        },
                      },
                    }}
                    options={membersData} // Replace with your options
                    renderInput={(params) => (
                      <StyledTextField colors={colors}
                        {...params}
                        label="Email"
                        variant="outlined"
                        inputRef={ref}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isMembersDataFetching ||
                              isMembersDataLoading ? (
                                <CircularProgress
                                  size={24}
                                  sx={{
                                    color: colors.primary.main,
                                  }}
                                />
                              ) : null}
                              {
                                params.InputProps
                                  .endAdornment
                              }
                            </>
                          ),
                        }}
                        error={Boolean(
                          errors.selectedEmails
                        )}
                        helperText={
                          errors.selectedEmails
                            ? errors.selectedEmails.message
                            : ""
                        }
                      />
                    )}
                    renderTags={() => null}
                  />
                )}
              />
              <Box
                display={"flex"}
                flexDirection={"row"}
                flexWrap={"wrap"}
                flexGeow={1}
                gap={4}
                maxHeight={"10.625rem"}
                overflow={"auto"}
              >
                {selectedEmails?.map((email, index) => (
                  <PlaceHolderButton
                    key={index}
                    onClose={() => handleChipDelete(email)}
                    text={email}
                  />
                ))}
              </Box>
            </Box>
            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={4}
              className="sitesContainer"
            >
              <H8TitleMedium>
                Select Sites to assign to users
              </H8TitleMedium>
              <Box
                display="flex"
                flexDirection="row"
                flexGrow={1}
              >
                <StyledFormControl fullWidth margin="none" colors={colors}>
                  {/* <InputLabel htmlFor="">Status</InputLabel> */}
                  <Controller
                    name="siteConfig"
                    control={control}
                    defaultValue={null}
                    rules={{
                      required:
                        "Site selection is required",
                      validate: (value) =>
                        Array.isArray(value) &&
                        value.length > 0
                          ? true
                          : "Please select at least one site",
                    }}
                    render={({
                      field: { onChange, value, ref },
                    }) => (
                      <Autocomplete
                        fullWidth
                        multiple
                        disableCloseOnSelect
                        onChange={(_, data) =>
                          onChange(data)
                        }
                        ListboxProps={{
                          sx: {
                            maxHeight: "250px",
                            overflowY: "auto",
                            backgroundColor: colors.background.paper,
                                color: colors.text.primary,
                                "&.selected": {
                                  backgroundColor:
                                    colors.primary.light,
                                },
                          },
                        }}
                        slotProps={{
                          popupIndicator: {
                            sx: { color: colors.text.muted , 
                            '&:hover': { color: colors.text.primary },
                          }
                          },
                          clearIndicator: {
                            sx: {
                              color: colors.text.muted,
                              '&:hover': { color: colors.text.primary },
                            },
                          },
                        }}
                        value={value || []}
                        clearIcon={null}
                        options={siteList} // Replace with your options
                        renderInput={(params) => (
                          <StyledTextField colors={colors}
                            {...params}
                            label="Site Name"
                            variant="outlined"
                            inputRef={ref}
                            sx={{ minHeight: "2.5rem" }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {isSiteGroupDataFetching ||
                                  isSiteGroupDataLoading ? (
                                    <CircularProgress
                                      size={24}
                                      sx={{
                                        color: colors.primary.main,
                                      }}
                                    />
                                  ) : null}
                                  {
                                    params.InputProps
                                      .endAdornment
                                  }
                                </>
                              ),
                            }}
                          />
                        )}
                        renderTags={() => null}
                      />
                    )}
                  />
                </StyledFormControl>
              </Box>
              <Box
                display={"flex"}
                flexDirection={"row"}
                gap={4}
                maxWidth={"62.5rem"}
                flexWrap={"wrap"}
              >
                {siteOptions?.map((site, index) => (
                  <PlaceHolderButton
                    key={index}
                    onClose={() =>
                      handleSiteConfigDeletion(site)
                    }
                    text={site}
                  />
                ))}
              </Box>
            </Box>
            <Box
              display={"flex"}
              flexDirection={"row"}
              justifyContent={"flex-end"}
              marginTop={6}
              gap={2}
            >
              <SecondaryButton colors={colors}
                onClick={() => {
                  onMoveBack();
                  reset();
                }}
              >
                Back
              </SecondaryButton>
              <PrimaryButton colors={colors}
                type="submit"
                disabled={!isValid}
              >
                Save
              </PrimaryButton>
            </Box>
          </Box>
        </form>
      </DialogContent>
    </>
  );
};

const SitePermissionsDialog = ({
  open,
  handleClose,
  orgId,
  refetch,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertText, setAlertText] = useState("");
  const [severityLevel, setSeverityLevel] = useState("");
  const [formData, setFormData] = useState({});
  const { colors } = useTheme();
  const onMoveForward = (data) => {
    setActiveStep((prev) => prev + 1);
  };
  const onMoveBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  return (
    <StyledDialog
      colors={colors}
      maxWidth="md"
      fullWidth
      open={open}
      onClose={handleClose}
    >
      {activeStep === 0 && (
        <ResourceSelectionForm
          onMoveForward={onMoveForward}
          setOpen={handleClose}
          setActiveStep={setActiveStep}
          setFormData={setFormData}
          formData={formData}
        />
      )}
      {activeStep === 1 && (
        <SitePermissionsForm
          open={open}
          handleClose={handleClose}
          onMoveBack={onMoveBack}
          setActiveStep={setActiveStep}
          refetch={refetch}
        />
      )}
    </StyledDialog>
  );
};

export const PermissionsDialog = ({
  open,
  handleClose,
  refetch,
  orgId,
}) => {
  const queryClient = new QueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <SitePermissionsDialog
        open={open}
        handleClose={handleClose}
        refetch={refetch}
      />
    </QueryClientProvider>
  );
};

export default PermissionsDialog;
